import React, {useEffect, useMemo, useState} from 'react';
import TopBar from '../../Components/TopBar';
import Page from '../../Components/Containers/Page';
import { useNavigate, useParams } from 'react-router-dom';
import { useWorkDate } from '../../Data/Jobs';
import { PageLoading } from '../../Components/Loading';
import Calendar from '../../Components/Calendar';
import Icon from '../../Components/Icons/Icon';
import Job from './Job';
import { getDateStr } from '../../Components/Formatting/DateTime';
import MarkAsDone from '../Customer/MarkAsDone';
import Skip from '../Customer/Skip';
import ScheduleJob from '../Customer/ScheduleJob';
import { useGrants } from '../../Data/Business';
import Dropdown from '../../Components/Dropdown';
import { usePreference } from '../../Data/Preference';
import { useSession } from '../../Components/Session';
import { Roles } from '../../Data/Users';

const Work = () => {
    const {date} = useParams();
    const navigate = useNavigate();
    const [assignedTo, setAssignedTo] = usePreference('work:assignedTo', null);
    const {jobs, refresh} = useWorkDate(date, assignedTo, true);
    const [markingJobDone, setMarkingJobDone] = useState(null);
    const [skipping, setSkipping] = useState(null);
    const [scheduling, setScheduling] = useState(null);
    const [expanded, setExpanded] = useState(null);

    const dateStr = useMemo(() => getDateStr(date, false, true), [date]);

    const expandNext = () => {
        const open = expanded ?? markingJobDone?.id ?? skipping?.id ?? scheduling?.id ?? null;

        if (!jobs || !open) {
            return;
        }

        const next = findNextJob(jobs, open);

        setExpanded(next ?? null);
    }

    const refreshAndExpandNext = () => {
        expandNext();
        refresh();
    }

    useEffect(() => {
        refresh();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [assignedTo]);

    return <>
        <TopBar
            left={<AssignedTo {...{assignedTo, setAssignedTo}} />}
            title={dateStr}
            right={<Calendar onSelect={d => navigate('/work/'+d.date)} selected={date} />}
        />

        <Page>
            {jobs ?
                <Jobs {...{jobs, dateStr, setMarkingJobDone, setSkipping, setScheduling, expanded, setExpanded}} /> :
                <PageLoading />
            }
        </Page>

        <MarkAsDone job={markingJobDone}
            onClose={() => setMarkingJobDone(null)}
            then={refreshAndExpandNext}
        />
        <Skip job={skipping}
            onClose={() => setSkipping(null)}
            then={refreshAndExpandNext}
        />
        <ScheduleJob job={scheduling}
            onClose={() => setScheduling(null)}
            then={refreshAndExpandNext}
        />
    </>
}

const AssignedTo = ({assignedTo, setAssignedTo}) => {
    const {activeGrants} = useGrants();
    const {isRoleSufficient} = useSession();

    if (!activeGrants || !isRoleSufficient(Roles.Manager)) {
        return <Dropdown
            disabled
            icon="User"
        />;
    }

    const opt = (title, value) => ({
        value,
        title,
        onClick: () => setAssignedTo(value),
        icon: assignedTo === value ? 'CircleCheck' : 'Circle',
    });

    return <Dropdown
        openRight
        icon="User"
        iconTheme={assignedTo ? 'solid' : null}
        className={assignedTo ? 'fill-brand' : null}
        options={[
            {section: 'Assigned to'},
            opt('Anyone', null),
            ...activeGrants.map(grant => opt(grant.title, grant.userId)),
        ]}
    />
}

const Jobs = ({jobs, dateStr, setMarkingJobDone, setSkipping, setScheduling, expanded, setExpanded}) => {

    if (jobs.length === 0)  {
        return <NoJobs dateStr={dateStr} />
    }

    return <div className="flex flex-col divide-y divide-gray-200 overflow-visible">
        {jobs.map(job => {
            const isExpanded = expanded === job.id;
            const toggleIsExpanded = () => setExpanded((exp) => exp === job.id ? null : job.id);

            return <Job key={job.id} {...{isExpanded, toggleIsExpanded, job, setMarkingJobDone, setSkipping, setScheduling}} />
        })}
    </div>
}

const NoJobs = ({dateStr}) => {
    return <div className="text-center mt-5">
        <Icon icon="UmbrellaBeach" size="3x" center />
        <p className="font-bold mt-3">No jobs scheduled for {dateStr}</p>
        <p className="text-muted mt-3">To add jobs to your work view<br />use the Plan tab or Customer's page</p>
    </div>
}

const findNextJob = (jobs, jobId) => {
    const index = jobs.findIndex(j => j.id === jobId);

    if (index === -1) {
        return null;
    }

    const next = jobs[index + 1] ?? null;

    return next ? next.id : null;
}

export default Work;
